import React from "react";
import { Modal, Button, Form, Input, Select, DatePicker, InputNumber } from "antd";
import Wrapper from "./style";
import { customBaseUrl, httpGet, httpPost } from "../../../store/http";
import { hideLoader, showLoader } from "../../../utils/loader";
import { NotificationManager } from "react-notifications";
import moment from "moment";

const { Option } = Select;

const MapProductGLModal = ({ showModal, hideModal, onSuccess }) => {
    const [form] = Form.useForm();
    const [products, setProducts] = React.useState([]);
    const [glAccounts, setGlAccounts] = React.useState([]);
    const [loadingLists, setLoadingLists] = React.useState(false);

    const fetchData = React.useCallback(async () => {
        setLoadingLists(true);
        try {
            const [prodRes, glRes] = await Promise.all([
                httpGet(`${customBaseUrl.integUrl}/api/v1/products?page=0&size=1000`),
                httpGet(`${customBaseUrl.tranv2}/api/v1/admin/gl-statistics/account/all`)
            ]);

            if (prodRes?.status) {
                setProducts(prodRes.data?.content || []);
            }
            if (glRes?.success) {
                setGlAccounts(glRes.data || []);
            }
        } catch (error) {
            console.error('Error fetching modal data:', error);
        } finally {
            setLoadingLists(false);
        }
    }, []);

    React.useEffect(() => {
        if (showModal) {
            fetchData();
        }
    }, [showModal, fetchData]);

    const handleMap = async (values) => {
        showLoader();
        const payload = {
            ...values,
            effectiveDate: values.effectiveDate ? values.effectiveDate.format('YYYY-MM-DD') : null,
            expiryDate: values.expiryDate ? values.expiryDate.format('YYYY-MM-DD') : null,
        };

        try {
            const res = await httpPost(`${customBaseUrl.tranv2}/api/v1/product-codes/mappings`, payload, false, null, false);
            if (res?.success) {
                NotificationManager.success('Product mapping created successfully!');
                hideModal();
                form.resetFields();
                if (onSuccess) onSuccess();
            } else {
                NotificationManager.error(res?.message || 'Failed to map GL account.');
            }
        } catch (error) {
            NotificationManager.error('An error occurred while mapping GL account.');
        } finally {
            hideLoader();
        }
    };

    return (
        <Modal
            visible={showModal}
            title="Map GL Account to Product Code"
            onCancel={hideModal}
            footer={null}
            centered
            width={600}
        >
            <Wrapper>
                <div className="title" style={{ marginBottom: 24, fontSize: 18, fontWeight: 700 }}>Product GL Mapping</div>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleMap}
                    initialValues={{
                        priority: 0,
                        accountType: 'SAVINGS'
                    }}
                >
                    <Form.Item
                        label="Product Code"
                        name="productCode"
                        rules={[{ required: true, message: 'Product code is required' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Select a product"
                            optionFilterProp="children"
                            loading={loadingLists}
                        >
                            {products.map(p => (
                                <Option key={p.productCode} value={p.productCode}>{p.productName} ({p.productCode})</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item
                            label="Debit GL Account Code"
                            name="debitGlAccountCode"
                            rules={[{ required: true, message: 'Debit GL account code is required' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select GL Account"
                                optionFilterProp="children"
                                loading={loadingLists}
                            >
                                {glAccounts.map(acc => (
                                    <Option key={acc.accountCode} value={acc.accountCode}>{acc.accountName} ({acc.accountCode})</Option>
                                ))}
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Credit GL Account Code"
                            name="creditGlAccountCode"
                            rules={[{ required: true, message: 'Credit GL account code is required' }]}
                        >
                            <Select
                                showSearch
                                placeholder="Select GL Account"
                                optionFilterProp="children"
                                loading={loadingLists}
                            >
                                {glAccounts.map(acc => (
                                    <Option key={acc.accountCode} value={acc.accountCode}>{acc.accountName} ({acc.accountCode})</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item
                            label="Account Type"
                            name="accountType"
                            rules={[{ required: true }]}
                        >
                            <Select placeholder="Select Account Type">
                                <Option value="SAVINGS">SAVINGS</Option>
                                <Option value="CURRENT">CURRENT</Option>
                                <Option value="LOAN">LOAN</Option>
                                <Option value="FIXED_DEPOSIT">FIXED DEPOSIT</Option>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            label="Priority"
                            name="priority"
                            rules={[{ required: true }]}
                        >
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <Form.Item
                            label="Effective Date"
                            name="effectiveDate"
                            rules={[{ required: true }]}
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>

                        <Form.Item
                            label="Expiry Date"
                            name="expiryDate"
                        >
                            <DatePicker style={{ width: '100%' }} />
                        </Form.Item>
                    </div>

                    <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            style={{
                                height: 48,
                                background: '#FF4B01',
                                border: 'none',
                                fontWeight: 600,
                                borderRadius: 8
                            }}
                        >
                            Create Mapping
                        </Button>
                    </Form.Item>
                </Form>
            </Wrapper>
        </Modal>
    );
};

export default MapProductGLModal;
